'use client';

import { useMemo } from 'react';
import type { ProcessedMessage } from '../utils/message-processing';
import type { UserInfo } from '../types';
import type { MessageListCallbacks } from './ChatMessageList';
import { UserMessageCard } from './UserMessageCard';

export type ChatUserMessageItemProps = {
  processedMessage: ProcessedMessage;
  user?: UserInfo;
  callbacks: MessageListCallbacks;
};

export function ChatUserMessageItem({
  processedMessage,
  user,
  callbacks,
}: ChatUserMessageItemProps) {
  const { message: uiMessage, isUserStreaming } = processedMessage;
  const { onEditUserMessage, inlineEdit, checkpoint } = callbacks;
  const messageId = uiMessage.id;
  const isEditing = inlineEdit?.editingMessageId === messageId;

  const inlineEditor = useMemo(() => {
    if (!inlineEdit || !isEditing) {
      return undefined;
    }

    return {
      editorRef: inlineEdit.editorRef,
      onSubmit: inlineEdit.onSubmit,
      onCancel: inlineEdit.onCancel,
      onMentionsTrigger: inlineEdit.onMentionsTrigger,
      isSubmitting: inlineEdit.isSubmitting,
      isCancelBlocked: inlineEdit.isCancelBlocked,
    };
  }, [inlineEdit, isEditing]);

  const checkpointActions = useMemo(() => {
    if (!checkpoint || isUserStreaming) {
      return undefined;
    }

    return {
      onRestoreCheckpoint: () => checkpoint.onRestoreCheckpoint(messageId),
      onRedoRestore:
        checkpoint.restoredMessageId === messageId
          ? checkpoint.onRedoRestore
          : undefined,
      isPending: checkpoint.isPending,
    };
  }, [checkpoint, isUserStreaming, messageId]);

  return (
    <UserMessageCard
      uiMessage={uiMessage}
      user={user}
      noHeader
      isLoading={isUserStreaming}
      onEditUserMessage={
        onEditUserMessage && !isUserStreaming
          ? () => onEditUserMessage(messageId)
          : undefined
      }
      inlineEditor={inlineEditor}
      checkpointActions={checkpointActions}
    />
  );
}
